// Gedeelde TypeScript types tussen frontend en backend.
// Single source of truth — gebruik via Zod schemas voor runtime validatie.
import { z } from 'zod';
import { schijvenConfigSchema } from './schijven.js';
import { gevelConfigSchema, gevelKeuzeSchema } from './gevel.js';
import type { WorkflowStaat } from './workflow.js';
import { extraRegelSchema } from './extraRegels.js';
import { opleveringSchema } from './opleveringSchema.js';

// === Eenheden ===
export const eenheidSchema = z.enum(['STUK', 'LM', 'M2', 'FORFAIT', '']);
export type Eenheid = z.infer<typeof eenheidSchema>;

// === Pricelist ===
export const pricelistItemSchema = z.object({
  id: z.string(),
  pricelistId: z.string(),
  pdfNaam: z.string(),
  eenheid: eenheidSchema,
  naamArbeid: z.string(),
  prijs: z.number(),
  isNvt: z.boolean(),
  sortOrder: z.number().int().default(0),
  /** Stabiele post-sleutel binnen een prijslijstfamilie (over versies heen). */
  code: z.string().nullable().optional(),
  /** Groepstitel uit de bronlijst (bv. "Afbraak", "Goten"). */
  groep: z.string().optional(),
  /** Volledige (meertalige) omschrijving voor de PDF naar de onderaannemer. */
  omschrijvingVolledig: z.string().optional(),
  /** Wat inbegrepen is in de post. */
  toelichting: z.string().optional(),
  /** Optionele schijven (progressief of per klasse) i.p.v. één vaste prijs. */
  schijven: schijvenConfigSchema.nullable().optional(),
  /** Codes van posten waarin deze post normaal al inbegrepen is (overlapcontrole). */
  inbegrepenIn: z.array(z.string()).optional(),
});
export type PricelistItem = z.infer<typeof pricelistItemSchema>;

/** Type werken van een prijslijst. 'algemeen' = de matchinglijsten van vóór oktober 2026. */
export const typeWerkenSchema = z.enum(['algemeen', 'gevel', 'dak_hellend']);
export type TypeWerken = z.infer<typeof typeWerkenSchema>;

export const pricelistSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string().default(''),
  currency: z.string().default('EUR'),
  createdAt: z.string(),
  updatedAt: z.string(),
  items: z.array(pricelistItemSchema).optional(),
  /** Stabiele sleutel van de prijslijstfamilie, bv. "gevel_A" — versies delen dezelfde code. */
  code: z.string().nullable().optional(),
  typeWerken: typeWerkenSchema.optional(),
  /** Gevelvariant A of B (enkel voor type gevel). */
  variant: z.string().nullable().optional(),
  versie: z.number().int().optional(),
  geldigVanaf: z.string().nullable().optional(),
});
export type Pricelist = z.infer<typeof pricelistSchema>;

// === Ploeg / Onderaannemer ===
export const ploegSchema = z.object({
  id: z.string(),
  team: z.string().min(1),
  firma: z.string(),
  email: z.string().default(''),
  telefoon: z.string().default(''),
  pricelistId: z.string(),
  /** Optionele referentie naar de Bouwflow subcontractor-group; gebruikt door de Bouwflow-picker bij /project/new. */
  bouwflowId: z.number().int().positive().nullable().optional(),
  /** Firmagegevens voor de PDF en het beheer (SPEC_workflow 2.2 en 7.1). */
  btwNummer: z.string().default(''),
  firmaAdres: z.string().default(''),
  createdAt: z.string(),
});
export type Ploeg = z.infer<typeof ploegSchema>;

// === Activity log entry ===
export const activityLogEntrySchema = z.object({
  id: z.string(),
  createdAt: z.number(),
  userId: z.string().nullable(),
  userDisplay: z.string(),
  userEmail: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  entityLabel: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  projectSleutel: z.string().nullable().optional(),
  categorie: z.string().nullable().optional(),
  persoon: z.string().optional(),
  reden: z.string().optional(),
});
export type ActivityLogEntry = z.infer<typeof activityLogEntrySchema>;

// === User ===
export const roleSchema = z.enum(['admin', 'user']);
export type Role = z.infer<typeof roleSchema>;

export const userPublicSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string(),
  role: roleSchema,
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
  /** Mag de pop-ups bevestigen — volgt uit de rol (SPEC 27okt 7). */
  magBevestigen: z.boolean().optional(),
  /** Rol in de hiërarchie (27okt 7). */
  rolId: z.string().nullable().optional(),
  rolNaam: z.string().nullable().optional(),
  niveau: z.number().int().nullable().optional(),
  magVrijgeven: z.boolean().optional(),
  magBeheren: z.boolean().optional(),
  gedeeldAccount: z.boolean().optional(),
  /** Persoon achter een gedeeld account, als opgegeven. */
  persoon: z.string().nullable().optional(),
});
export type UserPublic = z.infer<typeof userPublicSchema>;

// === Parsed PDF item ===
export const parsedItemSchema = z.object({
  id: z.string(),
  categorie: z.string(),
  werken: z.string(),
  geadviseerdeHoeveelheid: z.number(),
  werkelijkeHoeveelheid: z.number(),
  eenheid: z.string(),
});
export type ParsedItem = z.infer<typeof parsedItemSchema>;

// === Project meta uit PDF ===
export const projectMetaSchema = z.object({
  klantNaam: z.string().default(''),
  klantAdres: z.string().default(''),
  adviseur: z.string().default(''),
  projectTitel: z.string().default(''),
  /** Door AI uit de fiche gehaald — bv. "P-2360". Lege string als niet vermeld. */
  projectNummer: z.string().default(''),
  /** Door AI uit de fiche gehaald — korte uppercase teamnaam (bv. "ALBIN"). Lege string als niet vermeld. */
  teamNaam: z.string().default(''),
  /** Vrije-tekst opmerkingen / instructies die buiten de tabel op de opstartfiche staan. */
  opmerkingen: z.string().default(''),
});
export type ProjectMeta = z.infer<typeof projectMetaSchema>;

// === Matching ===
/**
 * Herkomst van een match — bepaalt wat de learning-laag ermee doet:
 *  - exact / contained / fuzzy: machine-beslissing, wordt NIET geleerd
 *  - learned: kwam uit het geheugen, telt enkel als herbevestiging
 *  - manual: gebruiker koos zelf de werkpost → wordt geleerd
 *  - manual-add: werkpost zonder PDF-regel (handmatig toegevoegd) → niet geleerd
 * Optioneel voor backwards-compat met reeds opgeslagen projecten; ontbreekt het,
 * dan geldt `autoMatched === false` als "manual".
 */
export const matchSourceSchema = z.enum(['exact', 'learned', 'contained', 'fuzzy', 'manual', 'manual-add']);
export type MatchSource = z.infer<typeof matchSourceSchema>;

export const matchedSubItemSchema = z.object({
  parsedItemId: z.string(),
  pdfNaam: z.string(),
  plItemId: z.string(),
  hoeveelheid: z.number(),
  eenheid: z.string(),
  prijs: z.number(),
  subtotaal: z.number(),
  confidence: z.number(),
  autoMatched: z.boolean(),
  matchSource: matchSourceSchema.optional(),
});
export type MatchedSubItem = z.infer<typeof matchedSubItemSchema>;

/** Reden-string die de UI zet als de gebruiker een item expliciet negeert. De
 *  server leert hieruit een "learned skip" voor dezelfde prijslijst. */
export const USER_SKIP_REDEN = 'Genegeerd door gebruiker';
/** Reden-string die de matcher zet als een item via het geheugen wordt overgeslagen. */
export const LEARNED_SKIP_REDEN = 'Eerder genegeerd (geleerd)';

// === Learning (geheugen per prijslijst) ===
export const learnedMatchRowSchema = z.object({
  id: z.string(),
  pdfNaamNormalized: z.string(),
  plItemId: z.string(),
  /** Denormalized voor weergave — kan leeg zijn als het item ondertussen verwijderd is. */
  plItemPdfNaam: z.string().default(''),
  plItemNaamArbeid: z.string().default(''),
  useCount: z.number(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  /** false = uitgezet: blijft bewaard maar wordt niet toegepast (SPEC_calculatie 10.2). Ontbreekt = aan. */
  actief: z.boolean().optional(),
});
export type LearnedMatchRow = z.infer<typeof learnedMatchRowSchema>;

export const learnedSkipRowSchema = z.object({
  id: z.string(),
  pdfNaamNormalized: z.string(),
  useCount: z.number(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  /** false = uitgezet: de regel komt weer in de review-queue (SPEC_calculatie 10.2). Ontbreekt = aan. */
  actief: z.boolean().optional(),
});
export type LearnedSkipRow = z.infer<typeof learnedSkipRowSchema>;

export const learningDataSchema = z.object({
  pricelistId: z.string(),
  matches: z.array(learnedMatchRowSchema),
  skips: z.array(learnedSkipRowSchema),
  /** Geleerde overlapbeslissingen (SPEC_wijzigingen 5.4) — per prijslijstfamilie. */
  overlaps: z.array(z.object({ sleutel: z.string(), beslissing: z.enum(['betalen', 'niet_betalen']) })).optional(),
});
export type LearningData = z.infer<typeof learningDataSchema>;

export const matchedLineSchema = z.object({
  id: z.string(),
  naamArbeid: z.string(),
  eenheid: z.string(),
  prijs: z.number().nullable(),
  hoeveelheid: z.number(),
  subtotaal: z.number(),
  consumedParsedItemIds: z.array(z.string()),
  autoMatched: z.boolean(),
  subItems: z.array(matchedSubItemSchema),
  /** Snapshot van de schijven van de prijslijstpost: het subtotaal volgt dan uit de ladder. */
  schijven: schijvenConfigSchema.optional(),
  /** Overlapcontrole (bv. Nedelco 5.4): de post zit normaal al in een andere post op de
   *  calculatie. 'open' telt niet mee tot de gebruiker beslist; 'bevestigd' = toch apart
   *  betalen; 'geweigerd' = niet betalen (blijft zichtbaar, telt niet mee). */
  overlap: z.object({
    itemCode: z.string(),
    inbegrepenInCode: z.string(),
    inbegrepenInNaam: z.string(),
    status: z.enum(['open', 'bevestigd', 'geweigerd']),
  }).optional(),
  /** Open voor oplevering (27okt 4): de onderaannemer mag de post nog niet factureren. */
  oplevering: opleveringSchema.optional(),
  /** Reden bij een prijs die afwijkt van de standaardprijs (27okt 2) — verplicht, min. 5 tekens. */
  prijsAfwijkingReden: z.string().optional(),
  /** Accessoire geprijsd uit de gevellijst A/B (SPEC_calculatie 7.1): code van de post. */
  gevelAccessoire: z.string().optional(),
});
export type MatchedLine = z.infer<typeof matchedLineSchema>;

export const meerwerkLineSchema = z.object({
  id: z.string(),
  pdfNaam: z.string(),
  naamArbeid: z.string(),
  categorie: z.string(),
  eenheid: z.string(),
  hoeveelheid: z.number(),
  prijs: z.number(),
  subtotaal: z.number(),
  parsedItemId: z.string(),
  plItemId: z.string().optional(),
  oplevering: opleveringSchema.optional(),
  prijsAfwijkingReden: z.string().optional(),
});
export type MeerwerkLine = z.infer<typeof meerwerkLineSchema>;

export const skippedItemSchema = z.object({
  parsedItemId: z.string(),
  pdfNaam: z.string(),
  reden: z.string(),
});
export type SkippedItem = z.infer<typeof skippedItemSchema>;

export const reviewItemSchema = z.object({
  parsedItem: parsedItemSchema,
  suggestions: z.array(z.object({
    item: pricelistItemSchema,
    score: z.number(),
  })),
});
export type ReviewItem = z.infer<typeof reviewItemSchema>;

export const chargeLineCategorieSchema = z.enum(['SCHADE', 'INHOUDING', 'BOETE', 'ANDERE']);
export type ChargeLineCategorie = z.infer<typeof chargeLineCategorieSchema>;

export const chargeLineSchema = z.object({
  id: z.string(),
  categorie: chargeLineCategorieSchema,
  omschrijving: z.string(),
  bedrag: z.number(), // positief getal — wordt afgetrokken van het totaal
});
export type ChargeLine = z.infer<typeof chargeLineSchema>;

export const totalsSchema = z.object({
  subtotaal: z.number(),
  meerwerk: z.number(),
  extraWerk: z.number().default(0),    // som van extra werken (SPEC_wijzigingen 3)
  aanrekening: z.number().default(0), // som van aanrekenbare kosten
  totaal: z.number(),                  // = te factureren = subtotaal + meerwerk + extraWerk - aanrekening
});
export type Totals = z.infer<typeof totalsSchema>;

export const matchResultSchema = z.object({
  matchedLines: z.array(matchedLineSchema),
  meerwerkLines: z.array(meerwerkLineSchema),
  skippedItems: z.array(skippedItemSchema),
  reviewItems: z.array(reviewItemSchema),
  unmatchedItems: z.array(reviewItemSchema),
});
export type MatchResult = z.infer<typeof matchResultSchema>;

// === AI usage ===
export const aiUsageSchema = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheCreateTokens: z.number(),
  durationMs: z.number(),
  model: z.string(),
  estimatedCostEur: z.number(),
});
export type AiUsage = z.infer<typeof aiUsageSchema>;

// === Project ===
export const projectStatusSchema = z.enum(['draft', 'matched', 'approved']);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

export const projectSchema = z.object({
  id: z.string(),
  klantNaam: z.string(),
  klantAdres: z.string(),
  adviseur: z.string(),
  projectTitel: z.string(),
  projectNummer: z.string().default(''),
  opmerkingen: z.string().default(''),
  ploegId: z.string(),
  pricelistSnapshotId: z.string(),
  pdfFilename: z.string(),
  createdAt: z.string(),
  createdByUserId: z.string(),
  updatedAt: z.string(),
  status: projectStatusSchema,
  parsedItems: z.array(parsedItemSchema),
  matchedLines: z.array(matchedLineSchema),
  meerwerkLines: z.array(meerwerkLineSchema),
  skippedItems: z.array(skippedItemSchema),
  chargeLines: z.array(chargeLineSchema).default([]),
  extraRegels: z.array(extraRegelSchema).default([]),
  gevel: gevelConfigSchema.nullable().default(null),
  totals: totalsSchema,
  /** Som van de gekoppelde Bouwflow-verkoopfacturen (excl. btw, netto van credits).
   *  Noemer voor de % arbeidskost; null → frontend valt terug op offertewaarde. */
  gefactureerd: z.number().nullable().default(null),
  /** Type werken uit Bouwflow (project_type): bv. "Dakwerken", "Gevelwerken", "Beide". */
  projectType: z.string().nullable().default(null),
});
export type Project = z.infer<typeof projectSchema>;

// === Project list (lichte versie voor dashboard) ===
export const projectListItemSchema = z.object({
  id: z.string(),
  klantNaam: z.string(),
  klantAdres: z.string(),
  projectNummer: z.string().default(''),
  ploegId: z.string(),
  ploegTeam: z.string(),
  ploegFirma: z.string(),
  pdfFilename: z.string(),
  createdAt: z.string(),
  status: projectStatusSchema,
  totaal: z.number(),
  offertewaarde: z.number().nullable().default(null),
  gefactureerd: z.number().nullable().default(null),
  projectType: z.string().nullable().default(null),
  /** Bouwflow project-id (intern) — voedt de deeplink naar Bouwflow. */
  bouwflowProjectId: z.number().nullable().default(null),
  /** OA-percentage op projectniveau (server-side, SPEC_wijzigingen 2.4). */
  pct: z.number().nullable().default(null),
  pctBasis: z.enum(['gefactureerd', 'offertewaarde']).nullable().default(null),
  /** Bevat een meetellende regel uit een nog niet verstuurde calculatie (2.5). */
  pctVoorlopig: z.boolean().default(false),
  /** Twee percentages op projectniveau (27okt 3). */
  percentages: z.lazy(() => tweePercentagesSchema).nullable().optional(),
  /** Bedrag open voor oplevering op deze calculatie (27okt 4). */
  openBedrag: z.number().default(0),
  /** Tags van het project (27okt 6). */
  tags: z.array(z.object({ id: z.string(), naam: z.string(), bron: z.enum(['manueel', 'automatisch']) })).default([]),
  versie: z.number().int().default(1),
  verstuurdeVersie: z.number().int().nullable().default(null),
  /** Laatste verzending naar de onderaannemer (ook van vóór het versiemodel). */
  verstuurdOp: z.string().nullable().default(null),
  oaGefactureerdOp: z.string().nullable().default(null),
  projectleider: z.string().nullable().default(null),
  /** Oplevering en eindfactuur uit Bouwflow (blok C); null = nog niet opgehaald of geen P-nummer. */
  workflow: z.custom<WorkflowStaat>().nullable().optional(),
});
export type ProjectListItem = z.infer<typeof projectListItemSchema>;

// === Twee percentages (27okt 3) ===
export const tweePercentagesSchema = z.object({
  /** Teller: kost(X) volgens SPEC_wijzigingen 2.4, inclusief open posten. */
  kost: z.number(),
  /** Deel van de teller dat open staat voor oplevering (27okt 4). */
  kostOpen: z.number(),
  offertewaarde: z.number().nullable(),
  gefactureerd: z.number().nullable(),
  opOfferte: z.number().nullable(),
  opGefactureerd: z.number().nullable(),
  /** Verschil in procentpunt tussen beide (null als één van beide ontbreekt). */
  verschilPp: z.number().nullable(),
  /** > 5 procentpunt verschil: onvolledige facturatie of niet-gefactureerd meerwerk. */
  waarschuwing: z.boolean(),
  /** Bevat een meetellende regel uit een niet-verstuurde calculatie (okt2026 2.5) of open posten. */
  voorlopig: z.boolean(),
});
export type TweePercentages = z.infer<typeof tweePercentagesSchema>;

/** Drempel voor de waarschuwing bij het verschil tussen beide percentages (27okt 3). */
export const PERCENTAGE_VERSCHIL_DREMPEL_PP = 5;

export function tweePercentages(input: {
  kost: number;
  kostOpen?: number;
  offertewaarde: number | null | undefined;
  gefactureerd: number | null | undefined;
  voorlopig?: boolean;
}): TweePercentages {
  const off = input.offertewaarde != null && input.offertewaarde > 0 ? input.offertewaarde : null;
  // Nog niets gefactureerd: "nog niet gefactureerd", geen 0 % en geen deling door nul.
  const gef = input.gefactureerd != null && input.gefactureerd > 0 ? input.gefactureerd : null;
  const opOfferte = off != null ? (input.kost / off) * 100 : null;
  const opGefactureerd = gef != null ? (input.kost / gef) * 100 : null;
  const verschilPp = opOfferte != null && opGefactureerd != null ? Math.abs(opGefactureerd - opOfferte) : null;
  return {
    kost: input.kost,
    kostOpen: input.kostOpen ?? 0,
    offertewaarde: off,
    gefactureerd: gef,
    opOfferte,
    opGefactureerd,
    verschilPp,
    waarschuwing: verschilPp != null && verschilPp > PERCENTAGE_VERSCHIL_DREMPEL_PP,
    voorlopig: !!input.voorlopig,
  };
}

// === % Arbeidskost ===
export interface ArbeidskostPct {
  /** Arbeidskost als % van de noemer. */
  pct: number;
  /** De gebruikte noemer (gefactureerd of, bij gebrek daaraan, offertewaarde). */
  noemer: number;
  basis: 'gefactureerd' | 'offertewaarde';
}

/**
 * Berekent de % arbeidskost van een project: `totaal / noemer * 100`, waarbij de
 * noemer het netto gefactureerde bedrag is, en bij gebrek daaraan de offertewaarde.
 * Eén bron van waarheid — gebruikt door dashboard, projectdetail en maandoverzicht.
 *
 * @returns null als er geen bruikbare (positieve) noemer is.
 */
export function arbeidskostPct(
  totaal: number,
  offertewaarde: number | null | undefined,
  gefactureerd: number | null | undefined,
): ArbeidskostPct | null {
  if (gefactureerd != null && gefactureerd > 0) {
    return { pct: (totaal / gefactureerd) * 100, noemer: gefactureerd, basis: 'gefactureerd' };
  }
  if (offertewaarde != null && offertewaarde > 0) {
    return { pct: (totaal / offertewaarde) * 100, noemer: offertewaarde, basis: 'offertewaarde' };
  }
  return null;
}

// === Auth API requests ===
export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const setupRequestSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1).max(100),
  password: z.string().min(10).max(200),
});
export type SetupRequest = z.infer<typeof setupRequestSchema>;

export const changePasswordRequestSchema = z.object({
  oldPassword: z.string().min(1),
  newPassword: z.string().min(10).max(200),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

// === Project create request ===
export const createProjectRequestSchema = z.object({
  klantNaam: z.string().min(1),
  klantAdres: z.string(),
  adviseur: z.string(),
  projectTitel: z.string(),
  projectNummer: z.string().default(''),
  opmerkingen: z.string().default(''),
  ploegId: z.string(),
  pdfFilename: z.string(),
  /** Optionele opslag-key uit /parse — server verplaatst de tijdelijke PDF naar de definitieve plaats.
   *  Strikt hex (32 tekens uit randomBytes(16)) — voorkomt path-traversal in de storage-laag. */
  pdfStorageKey: z.string().regex(/^[a-f0-9]{32}$/i, 'Ongeldige pdfStorageKey').optional(),
  /** Optionele referentie naar het Bouwflow Project — gevuld als de gebruiker via de Bouwflow-picker koos. */
  bouwflowProjectId: z.number().int().positive().nullable().optional(),
  /** Offertewaarde (totaal budget) van het project — uit Bouwflow of handmatig ingevuld. */
  offertewaarde: z.preprocess(
    (v) => (v === null || v === undefined || v === '' ? null : Number(v)),
    z.number().nullable().optional(),
  ),
  parsedItems: z.array(parsedItemSchema),
  matchedLines: z.array(matchedLineSchema),
  meerwerkLines: z.array(meerwerkLineSchema),
  skippedItems: z.array(skippedItemSchema),
  /** @deprecated vervangen door extraRegels (aanrekenbare_kost); enkel nog voor oude clients. */
  chargeLines: z.array(chargeLineSchema).default([]),
  extraRegels: z.array(extraRegelSchema).default([]),
  gevel: gevelKeuzeSchema.nullable().default(null),
  /** Matchinglijst (versie) die bij /parse gebruikt werd — anders die van de ploeg. */
  pricelistId: z.string().optional(),
  /** Client-totalen worden genegeerd: de server herberekent ze bij het opslaan. */
  totals: totalsSchema.optional(),
  status: projectStatusSchema,
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;
